import { RegionLoadState } from '@/components/feedback/RegionLoadState';
import { useEffect, useRef, useState } from 'react';
import { Button, Spinner, Text } from '@/components/primitives';
import { inlineButtonClass } from '@/components/primitives/Button/button.css';
import { Icons, vars } from '@/theme';
import type { StreamEntry } from '@/api/stream';
import { useStream, PAGE_SIZE } from '@/features/profile/hooks/useStream';
import { useStreamUiStore } from '@/store';
import { AnchoredCard, type AnchorRect } from './AnchoredCard';
import { StreamEntryRow } from './StreamEntryRow';
import { header, body, earlierToggle } from './notificationBell.css';

/**
 * NotificationsPanel — the activity stream as a boxed dropdown anchored under
 * the bell (not a full-height slide-out). Unread items show by default; already
 * read items collapse into an "Earlier" disclosure at the bottom, so the panel
 * stays small and focused on what's new.
 *
 * Rows never move between the two groups while the panel is open (#1244): an
 * entry seen unread at any point during this open — the first fetch after a
 * cold cache, the refetch on open, a later poll — stays in the unread group
 * until the panel closes, so reading it or "mark all read" doesn't make rows
 * jump between sections mid-view. Which rows show their details is held here,
 * not in the row, so a row that remounts in the other group keeps its details
 * open. Both are cleared on each open.
 */
export function NotificationsPanel({ anchor }: { anchor: AnchorRect | null }) {
  const panelOpen = useStreamUiStore((s) => s.panelOpen);
  const closePanel = useStreamUiStore((s) => s.closePanel);
  const {
    entries,
    unreadCount,
    isLoading,
    listFailed,
    restartList,
    refetchList,
    markEntriesRead,
    markAllRead,
    hasMoreUnread,
    loadMoreUnread,
    isLoadingMore,
    listUnreadCount,
  } = useStream();

  const [trackedOpen, setTrackedOpen] = useState(panelOpen);
  // Every entry seen unread during this open (latest copy). Paged lists can
  // drop a row that was just read (the server re-sorts it past the loaded
  // pages), so the copy is what keeps it in the unread group (#1244, #1077).
  const [seenUnread, setSeenUnread] = useState<ReadonlyMap<number, StreamEntry>>(() => new Map());
  const [expandedIds, setExpandedIds] = useState<ReadonlySet<number>>(() => new Set());
  const [showEarlier, setShowEarlier] = useState(false);

  // Per-open state, adjusted during render (React's "reset state when a prop
  // changes" pattern) so no frame renders with the previous open's groups.
  if (panelOpen !== trackedOpen) {
    setTrackedOpen(panelOpen);
    setSeenUnread(new Map());
    setExpandedIds(new Set());
    setShowEarlier(false);
  } else if (panelOpen) {
    // I1: the unread set only grows during an open, from whatever list data
    // useStream already has (no extra request).
    let next: Map<number, StreamEntry> | null = null;
    for (const e of entries) {
      const prev = seenUnread.get(e.id);
      if (prev ? prev !== e : !e.viewed) {
        next ??= new Map(seenUnread);
        next.set(e.id, e);
      }
    }
    if (next) setSeenUnread(next);
  }

  // Refetch the list once per open. `refetchList` is read through a ref because
  // it changes identity on every render (useStream rebuilds it) — depending on
  // it here would re-fire the effect in a loop.
  const refetchRef = useRef(refetchList);
  refetchRef.current = refetchList;
  useEffect(() => {
    if (panelOpen) refetchRef.current();
  }, [panelOpen]);

  const toggleExpanded = (id: number) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  if (!panelOpen || !anchor) return null;

  // A row stays in the unread group for this open if it was seen unread at any
  // point during it — so acknowledging it doesn't yank it away.
  const inUnreadGroup = (id: number, viewed: boolean) => !viewed || seenUnread.has(id);
  const unread = entries.filter((e) => inUnreadGroup(e.id, e.viewed));
  const earlier = entries.filter((e) => !inUnreadGroup(e.id, e.viewed));
  // A row seen unread that the (paged) list no longer carries stays put.
  const listed = new Set(entries.map((e) => e.id));
  const dropped = [...seenUnread.values()].filter((e) => !listed.has(e.id));
  if (dropped.length > 0) {
    unread.push(...dropped);
    unread.sort((a, b) => b.created.localeCompare(a.created) || b.id - a.id);
  }
  // Rows read during this open stay in the unread group (#1244) but no longer
  // count toward "Showing N of M unread" (#1077).
  const unreadShown = unread.filter((e) => !e.viewed).length;

  return (
    <AnchoredCard
      anchor={anchor}
      width={384}
      maxHeight="min(72vh, 560px)"
      onDismiss={closePanel}
      role="dialog"
      aria-label="Notifications"
      data-testid="notifications-panel"
    >
      <div className={header}>
        <Text variant="cardTitle" as="span">
          Notifications
        </Text>
        {/* Marks EVERY unread entry of the user in this context on the server
            (`markRead(undefined)` -> `entryIds: null`), shown in the dropdown or not. */}
        {!isLoading && unreadCount > 0 && (
          <Button onClick={markAllRead} className={inlineButtonClass}>
            Mark all read
          </Button>
        )}
      </div>

      <div className={body}>
        {isLoading ? (
          <div style={{ padding: '28px 0', textAlign: 'center' }}>
            <Spinner size={22} />
          </div>
        ) : listFailed ? (
          // A failed first load is the region's own failed state, never "No notifications yet" (#1561-g).
          <RegionLoadState state="failed" onRetry={restartList} />
        ) : entries.length === 0 ? (
          <div style={{ padding: '28px 16px', textAlign: 'center' }}>
            <Text color="secondary">No notifications yet</Text>
          </div>
        ) : (
          <>
            {unread.length > 0 ? (
              unread.map((entry) => (
                <StreamEntryRow
                  key={entry.id}
                  entry={entry}
                  onMarkRead={(id) => markEntriesRead([id])}
                  expanded={expandedIds.has(entry.id)}
                  onToggleExpanded={() => toggleExpanded(entry.id)}
                />
              ))
            ) : (
              <div style={{ padding: '22px 16px', textAlign: 'center' }}>
                <Text color="secondary">You&rsquo;re all caught up</Text>
              </div>
            )}

            {hasMoreUnread && listUnreadCount > unreadShown && (
              <button
                type="button"
                className={earlierToggle}
                onClick={loadMoreUnread}
                disabled={isLoadingMore}
                data-testid="notifications-show-more"
              >
                <Text as="span" variant="caption" color="secondary">
                  Showing {unreadShown} of {listUnreadCount} unread · Show{' '}
                  {Math.min(PAGE_SIZE, listUnreadCount - unreadShown)} more
                </Text>
              </button>
            )}

            {earlier.length > 0 && (
              <>
                <button
                  type="button"
                  className={earlierToggle}
                  onClick={() => setShowEarlier((v) => !v)}
                  aria-expanded={showEarlier}
                  data-testid="notifications-earlier-toggle"
                >
                  {showEarlier ? (
                    <Icons.arrowDropUp style={{ fontSize: 18, color: vars.color.neutral.subtle }} />
                  ) : (
                    <Icons.arrowDropDown style={{ fontSize: 18, color: vars.color.neutral.subtle }} />
                  )}
                  <Text as="span" variant="caption" color="secondary">
                    Earlier · {earlier.length}
                  </Text>
                </button>
                {showEarlier &&
                  earlier.map((entry) => (
                    <StreamEntryRow
                  key={entry.id}
                  entry={entry}
                  onMarkRead={(id) => markEntriesRead([id])}
                  expanded={expandedIds.has(entry.id)}
                  onToggleExpanded={() => toggleExpanded(entry.id)}
                />
                  ))}
              </>
            )}
          </>
        )}
      </div>
    </AnchoredCard>
  );
}
